import { useEffect, useState, useMemo, useRef } from "react";
import _ from 'underscore';
import { useTheme } from "../../../context/ThemeContext";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { dateConvert, getPreviousMonths } from "../../../utils/Helper";
import projectStore from "../../../Store/projectStore";

export const usePrecisionMatrix = () => {
    const [filterValues, setFilterValues] = useState({});
    const [precisionReport, setPrecisionReport] = useState({ show: false });
    const [summaryData, setSummaryData] = useState({ rankDistribution: {} });
    const [loader, setLoader] = useState(true);
    const { theme } = useTheme();
    const tagRef = useRef();
    useEffect(() => {
        // sessionStorage.removeItem("precision_lead");
        const months = getPreviousMonths(12);
        setFilterValues((prev) => ({ ...prev, months: [months?.[1]] }))
    }, [])

    useEffect(() => {
        if (filterValues?.apiCall) {
            sessionStorage.removeItem("precision_lead");
            getPrescisionSummary();
        }
    }, [filterValues?.apiCall]);


    const getPrescisionSummary = async () => {
        try {
            setLoader(true)
            const monthYearRange = filterValues?.months?.map(item => { return `${item?.month?.toString()?.padStart(2, "0")}/${item?.year}` });
            const payload = {
                "users": _.pluck(filterValues?.users, "user_code")?.toString() || "",
                "project_ids": _.pluck(filterValues?.projects, "project_id")?.toString() || "",
                "project_role_ids": _.pluck(filterValues?.project_role, "project_role_id")?.toString() || "",
                "project_lead_user_codes": filterValues?.precision_lead?.user_code || "",
                "month_year_range": monthYearRange?.toString() || "",
                "rate_range": "",
            };
            const res = await services.getInvokeRemote("POST", Urls("TGETPRECISIONREPORT"), payload);
            if (!res?.body?.error_status) {
                const data = res?.body?.data
                setSummaryData({
                    cardsData: data?.total_cards_data,
                    ratingDistributionData: data?.total_rating_contribution,
                    matricAverage: filterValues?.precision_lead?.user_code ? data?.lead_performance_avg : _.sortBy(data?.lead_performance_avg, "group_key"),
                    HeatMapData: [...(data?.lead_performance_matrix || [])].sort((a, b) => {
                        const dateRegex = /^(\d{2})\/(\d{4})$/;
                        const matchA = a.group_key?.match(dateRegex);
                        const matchB = b.group_key?.match(dateRegex);

                        if (matchA && matchB) {
                            const monthA = parseInt(matchA[1], 10);
                            const yearA = parseInt(matchA[2], 10);
                            const monthB = parseInt(matchB[1], 10);
                            const yearB = parseInt(matchB[2], 10);

                            if (yearA !== yearB) {
                                return yearA - yearB;
                            }
                            return monthA - monthB;
                        }
                        return (a.group_key || "").localeCompare(b.group_key || "");
                    })

                });
            }
            setLoader(false)
            setFilterValues((prev) => ({ ...prev, apiCall: false }))
        } catch (error) {
            setLoader(false)
            setFilterValues((prev) => ({ ...prev, apiCall: false }))
            console.error(" Error:", error);
        }
    };


    const cardObj = [
        { label: "Total Members", key: "total_member", icon: "fa-solid fa-users", render: (value) => <div>{value}</div> },
        { label: "Avg Precision", key: "team_Average", icon: "fa-solid fa-bullseye", render: (value) => <div>{value}/10</div> },
        { label: "Submissions", key: "total_submission", icon: "fa-solid fa-circle-check", render: (value) => <div>{value || 0}/{summaryData?.cardsData?.[0]?.total_member}</div> },
        { label: "Avg Team Delay", key: "average_delayed_submission", icon: "fa-solid fa-clock", render: (value) => value && <div>{value} Days</div> }
    ];

    const rankOptions = useMemo(() => {
        let seriesData = [];
        let values = [];
        summaryData?.ratingDistributionData?.forEach((item, index) => {
            const { critical_count = 0, below_average_count = 0, average_count = 0, good_count = 0, excellent_count = 0 } = item ?? {};
            seriesData?.push({
                name: item?.group_key,
                data: [
                    { y: critical_count || 0, color: '#f0baba', rangeVal: "<4.5", group_key: item?.group_key },
                    { y: below_average_count || 0, color: '#ffedd5', rangeVal: "4.5-5.9", group_key: item?.group_key },
                    { y: average_count || 0, color: '#fef3c7', rangeVal: "6.0-7.4", group_key: item?.group_key },
                    { y: good_count || 0, color: '#b6f2cb', rangeVal: "7.5-8.9", group_key: item?.group_key },
                    { y: excellent_count || 0, color: '#047857', rangeVal: ">=9.0", group_key: item?.group_key }
                ],
                dataLabels: { enabled: true }
            })
            values = values?.concat([critical_count, below_average_count, average_count, good_count, excellent_count]);
        })
        const rangeLabels = ['Critical (< 4.5)', 'Below Average (4.5-5.9)', 'Average (6.0-7.4)', 'Good (7.5-8.9)', "Excellent (>=9.0)"];
        const maxVal = values.length > 0 ? Math.max(...values) : 10;
        const isDark = theme === "dark";
        return {
            chart: {
                type: 'column',
                height: 320,
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff"
            },
            title: { text: '' },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                categories: rangeLabels,
                labels: { style: { color: isDark ? "#ffffff" : "#646464", fontSize: "12px", fontWeight: 600 } },

            },
            yAxis: {
                min: 0,
                max: maxVal,
                tickInterval: maxVal > 10 ? Math.ceil(maxVal / 5) : null,
                title: { text: null },
                gridLineWidth: 0,
                labels: {
                    enabled: true,
                    style: { color: isDark ? "#ffffff" : "#646464" }
                }
            },
            tooltip: {
                formatter: function () {
                    return `<b><span style="color:${this?.color};">\u25CF</span> ${this?.point?.category}</b><br/><span style="font-size:13px;">${this?.series?.name}<br/>Count: <b>${this?.y}</b></span>`
                },
            },
            plotOptions: {
                column: {
                    pointWidth: 30,
                    maxPointWidth: 40,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: false,
                        format: '{y}',
                        color: isDark ? "#ffffff" : "#646464"
                    }
                },
                series: {
                    point: {
                        events: {
                            click: function (event) {
                                setPrecisionReport({
                                    show: true,
                                    lead: "",
                                    range: this?.options?.rangeVal,
                                    group_key: this?.options?.group_key,
                                    fromRankDist: true
                                });
                            }
                        }
                    }
                }
            },
            series: seriesData
        };
    }, [summaryData, theme]);


    const performanceOptions = useMemo(() => {
        const isDark = theme === "dark";
        const label = _.pluck(summaryData?.matricAverage, "group_key")
        const leadCode = _.pluck(summaryData?.matricAverage, "manager_user_code")
        return {
            chart: {
                type: 'column',
                height: 320,
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff"
            },
            title: { text: '' },
            credits: { enabled: false },
            xAxis: {
                categories: label?.map(u => {
                    let label = u?.includes("/")
                    if (label) {
                        label = u?.split("/")
                        const date = `${label?.[0]}-01-${label?.[1]}`
                        label = dateConvert(date, "month-year")
                    } else {
                        label = u
                    }
                    return label
                }),
                labels: { style: { color: isDark ? "#ffffff" : "#333" } }
            },
            yAxis: {
                min: 0,
                max: 10,
                gridLineWidth: 0,
                title: { text: 'Rank Score', style: { color: isDark ? "#ffffff" : "#333" } },
                labels: { style: { color: isDark ? "#ffffff" : "#333" } }
            },
            series: [
                { name: 'Expected Rank', data: summaryData?.matricAverage?.map(u => Number(u.avg_combined_metrics?.toFixed(2))), color: '#3b82f680', dataLabels: { enabled: true } },
                { name: 'Given Rank', data: summaryData?.matricAverage?.map(u => Number(u.overall_rank_average?.toFixed(2))), color: '#19191ba8', dataLabels: { enabled: true } }
            ],
            legend: {
                itemStyle: { color: isDark ? "#ffffff" : "#333" },
                verticalAlign: 'top'
            },
            plotOptions: {
                column: {
                    pointWidth: 30,
                    maxPointWidth: 40,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: false,
                        format: '{y}',
                        color: isDark ? "#ffffff" : "#646464"
                    }
                }, series: {
                    point: {
                        events: {
                            click: function (event) {
                                setPrecisionReport({ show: true, group_key: leadCode[this.index] ? leadCode[this.index] : label[this.index] })
                            }
                        }
                    }
                }
            },
        };
    }, [theme, summaryData]);

    return { loader, cardObj, rankOptions, performanceOptions, precisionReport, setPrecisionReport, filterValues, setFilterValues, summaryData, tagRef };
};